import { useCallback } from 'react';
import { useThrottledToast } from '../../hooks/useThrottledToast';

import { Switch, PageHeader, Heading } from '../../shared/ui';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectUser, setUserData } from '../../store/auth.slice';
import { useChangeUserMutation } from '../../services/users.api';
import { useTariffToolCheck } from '../../hooks/useTariffToolCheck';

export const LifeBalanceWheelPage = () => {
  const tool = 'life_balance_wheel';
  const isActiveByTariff = useTariffToolCheck(tool);
  const dispatch = useAppDispatch();
  const userData = useAppSelector(selectUser);
  const [changeUser] = useChangeUserMutation();
  const showToast = useThrottledToast();

  const handleCheck = useCallback(
    async (a: boolean) => {
      if (!isActiveByTariff) return;
      if (!userData) return;
      try {
        if (userData?.active_psy_tools?.includes(tool)) {
          const arr = userData?.active_psy_tools.filter((el) => el !== tool);
          dispatch(setUserData({ ...userData, active_psy_tools: arr }));
          await changeUser({
            id: userData.id,
            active_psy_tools: arr,
          });
        } else {
          dispatch(
            setUserData({
              ...userData,
              active_psy_tools: [...userData?.active_psy_tools, tool],
            }),
          );
          await changeUser({
            id: userData.id,
            active_psy_tools: [...userData?.active_psy_tools, tool],
          });
        }
        showToast('success');
      } catch (error) {
        showToast('error');
      }
    },
    [changeUser, dispatch, isActiveByTariff, userData],
  );

  return (
    <div className="pb-[80px]">
      <PageHeader backTo="/tools" title="Колесо баланса" />

      <div className="space-y-6 text-ui text-ink-muted leading-relaxed mb-8">
        <section>
          <Heading size="sm" className="mb-2">
            Цель и назначение
          </Heading>
          <p>
            «Колесо жизненного баланса» — цифровой инструмент ZiNGiZi для диагностики и планирования изменений. Он
            помогает клиенту оценить состояние восьми ключевых сфер жизни и сформулировать конкретные шаги уже во время
            видео-сессии.
          </p>
        </section>

        <div className="flex items-center gap-5 mb-[40px]">
          <Switch
            disabled={!isActiveByTariff}
            checked={userData?.active_psy_tools?.includes(tool)}
            onChange={handleCheck}
            label={'Использовать Колесо баланса в видео-сессиях'}
          />
        </div>

        <section>
          <Heading size="sm" className="mb-2">
            Теоретическая основа
          </Heading>
          <p>
            Метод опирается на подходы практической психологии, коучинга и позитивной психотерапии. В онлайн-версии
            сохраняются классические принципы визуализации «колеса», дополненные интерактивными подсказками и журналом.
          </p>
        </section>

        <section>
          <Heading size="sm" className="mb-2">
            Как работает инструмент
          </Heading>
          <ol className="list-decimal pl-5 space-y-2 mt-2">
            <li>
              Психолог включает инструмент и объясняет шкалу оценки (от 1 до 10). Клиент видит колесо и сферы жизни.
            </li>
            <li>Клиент отмечает степень удовлетворённости каждой сферы. Все изменения синхронно видны психологу.</li>
            <li>После подтверждения результатов психолог получает готовую визуализацию и фиксирует её в журнале.</li>
            <li>На основе рисунка колеса формулируются гипотезы, задачи на сессию и домашние задания.</li>
          </ol>
        </section>

        <section>
          <Heading size="sm" className="mb-2">
            Преимущества цифрового формата
          </Heading>
          <ul className="list-disc pl-5 space-y-2">
            <li>Совместная работа в реальном времени: клиент и психолог видят изменения одновременно.</li>
            <li>Возможность сохранить результаты и сравнивать динамику между сессиями.</li>
            <li>Интерфейс адаптирован для компьютеров и мобильных устройств, не отвлекает от диалога.</li>
            <li>Данные автоматически попадают в терапевтический журнал.</li>
          </ul>
        </section>

        <section>
          <Heading size="sm" className="mb-2">
            Заключение
          </Heading>
          <p>
            «Колесо баланса» в ZiNGiZi превращает классическую технику в цифровой модуль для онлайн-консультаций. Это
            удобный способ быстро увидеть перекосы, выстроить фокус обсуждения и договориться о конкретных действиях с
            клиентом.
          </p>
        </section>
      </div>
    </div>
  );
};
